import { motion } from 'framer-motion';
import { UserCheck, Users, ClipboardCheck, UserPlus, Award } from 'lucide-react';
import Section from '../components/Section';
import Button from '../components/Button';
import PageHero from '../components/PageHero';
import { SF_UI } from '../components/ui/tokens';

const HowItWorksPage = () => {
  const steps = [
    {
      icon: <UserCheck size={40} />,
      title: 'Select Your Sports Office Officer',
      description: 'Browse our network of regional sports officers and select the one that best matches your location and sport.',
      delay: 0
    },
    {
      icon: <ClipboardCheck size={40} />,
      title: 'Complete Your Application',
      description: 'Fill out our comprehensive application form with details about your sporting achievements, goals, and needs.',
      delay: 0.1
    },
    {
      icon: <Users size={40} />,
      title: 'Community & AI Review',
      description: 'Your application is reviewed by our community of sports experts and our AI system for optimal matching.',
      delay: 0.2
    },
    {
      icon: <UserPlus size={40} />,
      title: 'Profile Creation',
      description: 'Once approved, we\'ll create your athlete profile on our platform, showcasing your talents and goals.',
      delay: 0.3
    },
    {
      icon: <Award size={40} />,
      title: 'Receive Support & Funding',
      description: 'Start receiving funding and support from our community network to achieve your sporting ambitions.',
      delay: 0.4
    }
  ];

  return (
    <>
      <PageHero
        eyebrow="How it works"
        title="How SportsFuture Works"
        subtitle="SportsFuture combines advanced technology with a human-centered mission to create a revolutionary platform for athletes worldwide. Here's how you can get involved and benefit from our ecosystem."
      />
      <Section bgColor={SF_UI.sectionBand} className="pt-12">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-12">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: step.delay }}
                className={`flex flex-col ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                } gap-8 items-center`}
              >
                <div className="md:w-1/4 flex justify-center">
                  <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center text-primary">
                    {step.icon}
                  </div>
                </div>
                <div className="md:w-3/4">
                  <div className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system">
                    <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section bgColor="bg-primary-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center">Our Technology Stack</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system"
            >
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2">Apertum Blockchain</h3>
              <p className="text-gray-600 text-center">
                Our platform is built on the secure and scalable Apertum blockchain, ensuring
                transparent transactions and immutable record-keeping.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system"
            >
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2">DAO1 Governance</h3>
              <p className="text-gray-600 text-center">
                We leverage DAO1's decentralized governance mechanisms to ensure fair
                distribution of funds and community-driven decision making.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system"
            >
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2">AI-Powered Matching</h3>
              <p className="text-gray-600 text-center">
                Our sophisticated AI algorithms match athletes with the most suitable
                funding opportunities and community supporters.
              </p>
            </motion.div>
          </div>
        </div>
      </Section>

      <Section>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center">Benefits for Athletes</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system border-l-4 border-primary"
            >
              <h3 className="text-lg font-semibold mb-2">Financial Support</h3>
              <p className="text-gray-600">
                Access to grants, scholarships, and community funding to support training,
                equipment, competition fees, and other expenses.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system border-l-4 border-primary"
            >
              <h3 className="text-lg font-semibold mb-2">Global Community</h3>
              <p className="text-gray-600">
                Connection to a worldwide network of athletes, coaches, sponsors, and
                supporters who can provide guidance and opportunities.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system border-l-4 border-primary"
            >
              <h3 className="text-lg font-semibold mb-2">Visibility</h3>
              <p className="text-gray-600">
                Enhanced exposure to potential sponsors and fans through our platform,
                helping you build your personal brand and career.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system border-l-4 border-primary"
            >
              <h3 className="text-lg font-semibold mb-2">Resource Access</h3>
              <p className="text-gray-600">
                Priority access to training resources, educational materials, and
                career development opportunities within the sports industry.
              </p>
            </motion.div>
          </div>
          
          <div className="text-center">
            <Button to="/apply" variant="primary" size="lg">
              Apply Now
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
};

export default HowItWorksPage;